/** @jsxImportSource @emotion/react */
'use memo'

import { App } from 'antd'
import React, { useState } from 'react'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import type {
  FinderPreference,
  FinderPreferenceInput,
} from '@sportninja/common/features/profile/finderSettings/types'
import type { SeedAddress } from '@sportninja/common/features/profile/finderSettings/finderPreferenceForm'
import { applyFinderApiError } from '@sportninja/common/features/profile/finderSettings/applyFinderApiError'
import { FINDER_SUCCESS_MESSAGES } from '@sportninja/common/features/profile/finderSettings/constants'
import { formatSuccessToast } from 'src/helpers/formatSuccessToast'
import WarningModal from 'src/components/WarningModal'
import { useFinderPreferenceMutations } from './useFinderPreferenceMutations'
import PreferenceModal from '../components/PreferenceModal'

interface Options {
  preferences: FinderPreference[]
  genderId: number | null
  /** SN-8295 — the player's profile address: an Add's location starts from
   * it when it is complete (`addSeed`). An Edit starts from the card's own. */
  profileAddress?: SeedAddress | null
}

interface UseFinderPreferenceActionsResult {
  openAdd: () => void
  handleEdit: (preference: FinderPreference) => void
  handleRemove: (preference: FinderPreference) => void
  modals: React.ReactNode
}

/**
 * Owns the add/edit/remove modal state + mutation wiring for the Player
 * Finder tab. `editing` is captured once on click (not re-derived from a
 * `.find()` against the live preferences list) so a background React Query
 * refetch can't swap out the preference mid-edit.
 */
export const useFinderPreferenceActions = (
  playerId: string,
  { preferences, genderId, profileAddress }: Options
): UseFinderPreferenceActionsResult => {
  const { message } = App.useApp()
  const { add, update, remove } = useFinderPreferenceMutations(playerId)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<FinderPreference | null>(null)
  const [deleting, setDeleting] = useState<FinderPreference | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  const openAdd = () => {
    setFieldErrors({})
    setEditing(null)
    setModalOpen(true)
  }

  const handleEdit = (preference: FinderPreference) => {
    setFieldErrors({})
    setEditing(preference)
    setModalOpen(true)
  }

  const handleRemove = (preference: FinderPreference) => {
    setDeleting(preference)
  }

  const closeModal = () => setModalOpen(false)

  const clearFieldError = (field: string) =>
    setFieldErrors((prev) => {
      if (!(field in prev)) {
        return prev
      }
      const next = { ...prev }
      delete next[field]
      return next
    })

  const handleSubmit = (input: FinderPreferenceInput, prefId?: string) => {
    const onDone = {
      onSuccess: () => {
        setFieldErrors({})
        message.success(
          formatSuccessToast(
            prefId ? FINDER_SUCCESS_MESSAGES.edit : FINDER_SUCCESS_MESSAGES.add
          )
        )
        setModalOpen(false)
      },
      onError: (error: Error) =>
        applyFinderApiError(error, {
          setFieldErrors,
          notifyError: message.error,
        }),
    }
    if (prefId) {
      update.mutate({ prefId, input }, onDone)
    } else {
      add.mutate({ input }, onDone)
    }
  }

  const confirmDelete = () => {
    if (!deleting) {
      return
    }
    remove.mutate(
      { prefId: deleting.id },
      {
        onSuccess: () => setDeleting(null),
        onError: (e) => message.error(e.message),
      }
    )
  }

  const modals = (
    <>
      <PreferenceModal
        isOpen={modalOpen}
        onClose={closeModal}
        editingPreference={editing}
        excludeSportIds={preferences.map(
          // Exclude each preference by the sport it actually occupies: for a
          // basic-stats (generic) pref that's its specific `generic_sport_id`
          // (NOT the shared parent `sport_id`, which would hide EVERY basic-stats
          // sport); for a full-stats pref it's the `sport_id`.
          (p) => p.generic_sport_id || p.sport_id
        )}
        genderId={genderId}
        profileAddress={profileAddress}
        onSubmit={handleSubmit}
        submitting={add.isPending || update.isPending}
        fieldErrors={fieldErrors}
        onClearFieldError={clearFieldError}
      />
      <WarningModal
        isOpen={deleting != null}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        title={capitalizeWords('Remove Preference')}
        description='Remove this Player Finder preference?'
        cancelLabel='CANCEL'
        confirmLabel='REMOVE'
        loadingLabel='REMOVING…'
        isLoading={remove.isPending}
        destructive
      />
    </>
  )

  return { openAdd, handleEdit, handleRemove, modals }
}
